import { create } from 'zustand';
import type {
  Bootstrap, DerivedCategory, PriorityEntry, Scenario, ScenarioEvent, ScenarioItem,
} from '@shared/types';
import { deriveCategories, withScenarioItems } from '@shared/groups';
import { rebalance, autoBalance, takeHomeIncome, type BalanceMode } from '@shared/project';
import { hasPayCalendar } from '@shared/income';
import type { NetBasis } from '@shared/calendar';
import { api } from './api';
import { getNetBasis, subscribeNetBasis } from './useNetBasis';

interface State {
  loading: boolean;
  error: string | null;
  data: Bootstrap | null;

  /** active expenses only — what the Dashboard, Expenses and Sankey read */
  cats: DerivedCategory[];
  /**
   * Same rollup, but paused/inactive expenses are included too (each starting
   * at a 0% dial, so nothing is switched on just by appearing here). Only the
   * Projections dial table and its scenario math read this one — everywhere
   * else needs the Notion-as-it-stands picture `cats` gives.
   */
  allCats: DerivedCategory[];
  /**
   * `allCats` before this scenario's what-if expenses are folded in.
   *
   * Held separately because the merge has to restart from Notion every time.
   * Folding into the previous merged result compounds: add an item twice and
   * remove it once, and the category still carries it.
   */
  baseAllCats: DerivedCategory[];
  /** active scenario — the working copy, i.e. saved state plus any unsaved draft */
  scenario: Scenario | null;
  /**
   * The active scenario exactly as the server has it.
   *
   * Kept alongside the working copy so "discard" is a reversion rather than a
   * refetch, and so `dirty` is a comparison rather than a flag we have to
   * remember to clear in nine different mutators.
   */
  saved: Scenario | null;
  /** working copy differs from `saved` — there is something to save */
  dirty: boolean;
  /** capped effective dials — derived, never persisted */
  effective: Record<string, number>;

  load: () => Promise<void>;
  selectScenario: (id: string) => void;

  // No `commit` flag any more: an edit is never a network write, so there is
  // nothing for the drag-end to flush. Save is an explicit user action.
  setItemDial: (itemId: string, pct: number) => void;
  setCatDial: (catKey: string, pct: number) => void;
  resetCat: (catKey: string) => void;
  resetItem: (itemId: string) => void;
  resetAllDials: () => void;

  setEvents: (events: ScenarioEvent[]) => void;

  /**
   * What-if expenses. Every one of these is an `edit()`, so they land in the
   * draft, mark the scenario dirty, and become durable only on Save — exactly
   * like a dial. A hypothetical that saved itself the moment you typed it would
   * be a worse trap than one that needs saving.
   */
  addScenarioItem: (item: Omit<ScenarioItem, 'id'>) => void;
  updateScenarioItem: (id: string, patch: Partial<Omit<ScenarioItem, 'id'>>) => void;
  removeScenarioItem: (id: string) => void;
  setPriority: (priority: PriorityEntry[]) => void;
  patchScenario: (patch: Partial<Scenario>) => void;

  /** Write the working copy back to the scenario it came from. */
  saveScenario: () => Promise<void>;
  /** Branch the working copy into a new scenario, leaving the source as it was saved. */
  saveScenarioAs: (name: string) => Promise<string>;
  /** Throw the draft away and go back to the saved state. */
  discardDraft: () => void;
  saving: boolean;

  autoBalance: (mode?: BalanceMode) => void;
  /** Snapshot taken before the last auto-balance, so it's reversible. */
  undoDials: Record<string, number> | null;
  undoAutoBalance: () => void;
  /** Refresh everything — Notion, then YNAB reconciled against it. */
  sync: () => Promise<void>;
  syncing: boolean;
  /**
   * Notion synced but YNAB didn't.
   *
   * Its own field rather than `error`, because it is a partial success and the
   * dashboard it just refreshed is correct — reporting it as a failed sync
   * would send you looking for a Notion problem that isn't there.
   */
  syncWarning: string | null;

  /**
   * Write one `app_settings` row and reflect it locally straight away.
   *
   * `usePreferenceSync` owns the theme keys; this is for the rest — flags that
   * live in the same table but have no client-side home of their own, and want
   * the UI to respond on the tap rather than after the next bootstrap.
   */
  setSetting: (key: string, value: string) => void;

  /**
   * The live net basis changed — recompute the cap against the new axis.
   *
   * Wired to `subscribeNetBasis` at module scope below. Not an edit: it never
   * writes to `scenario.dials`, never marks the scenario dirty and never
   * touches the network.
   */
  onBasisChange: () => void;
}

// ───────────────────────── drafts ─────────────────────────
//
// Edits used to go straight to the server on a debounce, which meant the
// scenario you were looking at absorbed every experimental drag. Duplicating
// afterwards cloned the *already-mutated* scenario, so "save as new" read as
// "save over Baseline" — the original was gone before the branch was taken.
//
// Now an edit only ever touches a draft. The draft is per-scenario and lives in
// localStorage, so it survives a reload and switching scenarios and back, but
// nothing reaches the database until Save or Save as new.

const draftKey = (id: string) => `vudget.draft.${id}`;

/*
 * The draft write is debounced — a slider drag would otherwise serialise the
 * whole scenario 60 times a second — but a debounced write is a write that can
 * land *after* the thing it was writing has been saved or discarded. Two ways
 * that bites, both of which the naive version got wrong:
 *
 *   save-as-new → clearDraft(source) → pending write fires → source is dirty
 *     again, holding the edits that were supposed to leave with the branch;
 *   edit → switch scenario within 400ms → pending write never flushes and the
 *     last edit is silently lost.
 *
 * So the pending write is tracked explicitly: `clearDraft` cancels it and
 * `flushDraft` forces it out before anything reads storage.
 */
let draftTimer: ReturnType<typeof setTimeout> | null = null;
let draftPending: { id: string; s: Scenario } | null = null;

function flushDraft() {
  if (draftTimer) { clearTimeout(draftTimer); draftTimer = null; }
  if (!draftPending) return;
  const { id, s } = draftPending;
  draftPending = null;
  try { localStorage.setItem(draftKey(id), JSON.stringify(s)); } catch { /* quota / private mode */ }
}

function writeDraft(id: string, s: Scenario) {
  if (draftPending && draftPending.id !== id) flushDraft();
  draftPending = { id, s };
  if (draftTimer) clearTimeout(draftTimer);
  draftTimer = setTimeout(flushDraft, 400);
}

function readDraft(id: string): Scenario | null {
  flushDraft();
  try {
    const raw = localStorage.getItem(draftKey(id));
    return raw ? (JSON.parse(raw) as Scenario) : null;
  } catch {
    return null;
  }
}

function clearDraft(id: string) {
  if (draftPending?.id === id) {
    if (draftTimer) { clearTimeout(draftTimer); draftTimer = null; }
    draftPending = null;
  }
  try { localStorage.removeItem(draftKey(id)); } catch { /* private mode */ }
}

/** Compare only the fields an edit can touch — name and timestamps are not draft state. */
function differs(a: Scenario, b: Scenario): boolean {
  const pick = (s: Scenario) => JSON.stringify([
    s.months, s.marketReturnPct, s.capAtIncome, s.income, s.dials, s.events, s.priority,
    s.items ?? [],
  ]);
  return pick(a) !== pick(b);
}

/**
 * The income figure the cap is measured against, on the axis currently live.
 *
 * Guaranteed is only offered when the pay calendar is actually known — without
 * it every stream falls back to an accrual twelfth, so the two bases are the
 * same number and quoting the guaranteed one would be a distinction the data
 * can't support. Same test `ProjectionsRoute` applies before showing the
 * toggle, so the cap and the meter can never disagree about which axis is live.
 */
function incomeOn(data: Bootstrap, basis: NetBasis): number {
  const known = hasPayCalendar(data.income, data.fundingSchedules ?? []);
  return basis === 'guaranteed' && known
    ? data.income.netGuaranteedMonthly
    : data.income.netMonthly;
}

/** Push a working copy to a scenario id. The one place drafts become durable. */
async function pushScenario(id: string, s: Scenario) {
  await api.patchScenario(id, {
    months: s.months,
    marketReturnPct: s.marketReturnPct,
    capAtIncome: s.capAtIncome,
    income: s.income,
  });
  // Items before dials: a dial keyed to a what-if that the server has not been
  // told about yet is a dangling reference for as long as the two requests are
  // in flight, and `putDials` prunes against the expense table.
  await api.putScenarioItems(id, s.items ?? []);
  await Promise.all([
    api.putDials(id, s.dials),
    api.putEvents(id, s.events),
    api.putPriority(id, s.priority),
  ]);
}

export const useStore = create<State>((set, get) => {
  /**
   * Recompute effective dials from intent.
   *
   * Always restarts from intent so freeing up room automatically flows back to
   * whatever the cap trimmed — restoration isn't a separate code path.
   */
  function recompute(protectKey?: string | null) {
    const { allCats, scenario, data } = get();
    if (!scenario || !data) return;
    // Both sides of the cap read the same axis. Trimming an accrual total
    // against a guaranteed ceiling would cut roughly 8% of the plan away the
    // moment the toggle was pressed — a basis flip is a change of units, never
    // a change of plan.
    const basis = getNetBasis();
    set({
      effective: rebalance(allCats, scenario.dials, {
        capAtIncome: scenario.capAtIncome,
        // Deductions dialled off base move take-home, so the cap measures
        // against the paycheck that actually lands — the same figure the meter
        // shows and `runProjection` spends, never a static income that ignored
        // the deduction dials.
        income: takeHomeIncome(incomeOn(data, basis), allCats, scenario.dials, basis),
        priority: scenario.priority,
        protectKey,
        basis,
      }),
    });
  }

  /** Rebuild `allCats` from Notion plus whatever the working scenario invents. */
  function remerge() {
    const { baseAllCats, scenario, data } = get();
    set({
      allCats: withScenarioItems(
        baseAllCats, scenario?.items ?? [], data?.fundingSchedules ?? [],
      ),
    });
  }

  /**
   * Apply an edit to the working copy.
   *
   * Every mutator funnels through here so there is exactly one place that
   * decides an edit is local, marks the scenario dirty, and schedules the draft
   * write. Previously each mutator called its own debounced network save, which
   * is how the dial path and the settings path ended up with different
   * durability semantics.
   */
  function edit(next: Scenario, protectKey?: string | null) {
    const { saved, scenario } = get();
    set({ scenario: next, dirty: saved ? differs(next, saved) : true });
    // Only when the what-if list actually moved. Re-deriving sixty categories
    // on every slider commit would put the merge in the drag path for no reason.
    if (scenario?.items !== next.items) remerge();
    recompute(protectKey);
    writeDraft(next.id, next);
  }

  /** Adopt a server scenario as the working copy, re-applying any stored draft. */
  function adopt(server: Scenario | null) {
    if (!server) {
      set({ scenario: null, saved: null, dirty: false });
      return;
    }
    const draft = readDraft(server.id);
    // A draft for a scenario that has since been saved elsewhere is stale, not
    // a change — dropping it is what keeps `dirty` from being permanently true.
    const working = draft && differs(draft, server) ? draft : server;
    set({ scenario: working, saved: server, dirty: working !== server });
    if (working === server) clearDraft(server.id);
  }

  return {
    loading: true,
    error: null,
    data: null,
    cats: [],
    allCats: [],
    baseAllCats: [],
    scenario: null,
    saved: null,
    dirty: false,
    effective: {},
    undoDials: null,
    syncing: false,
    syncWarning: null,
    saving: false,

    async load() {
      set({ loading: true, error: null });
      try {
        const data = await api.bootstrap();
        // Schedules go in so every item carries both monthly axes. Without them
        // `guaranteedBase` falls back to `base` and the toggle silently does
        // nothing to the numerators — which is the defect this fixes.
        const schedules = data.fundingSchedules ?? [];
        const cats = deriveCategories(
          data.expenses, data.recurrences, data.gfsBudget, data.categories, data.cspGroups,
          { schedules });
        // The Notion-only rollup, kept as the base that what-ifs are folded into
        // on every edit. Merging into the previous merged result would compound
        // — add an item twice, remove it once, and the category still carries it.
        const baseAllCats = deriveCategories(
          data.expenses, data.recurrences, data.gfsBudget, data.categories, data.cspGroups,
          { includeInactive: true, schedules });
        const allCats = baseAllCats;
        const server =
          data.scenarios.find(s => s.id === data.activeScenarioId) ??
          data.scenarios.find(s => s.isDefault) ??
          data.scenarios[0] ??
          null;
        set({ data, cats, baseAllCats, allCats, loading: false });
        adopt(server);
        // `adopt` may have restored a draft carrying what-ifs, so the merged
        // rollup is rebuilt from the scenario that actually won.
        remerge();
        recompute();
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e), loading: false });
      }
    },

    selectScenario(id) {
      const { data } = get();
      adopt(data?.scenarios.find(s => s.id === id) ?? null);
      remerge();
      recompute();
      api.putSettings({ active_scenario_id: id }).catch(() => {});
    },

    setItemDial(itemId, pct) {
      const { scenario } = get();
      if (!scenario) return;
      edit({ ...scenario, dials: { ...scenario.dials, [itemId]: pct } });
    },

    setCatDial(catKey, pct) {
      const { scenario, allCats } = get();
      if (!scenario) return;
      const cat = allCats.find(c => c.key === catKey);
      if (!cat) return;
      const dials = { ...scenario.dials };
      for (const i of cat.items) dials[i.id] = pct;
      edit({ ...scenario, dials }, catKey);
    },

    resetCat(catKey) {
      const { scenario, allCats } = get();
      if (!scenario) return;
      const cat = allCats.find(c => c.key === catKey);
      if (!cat) return;
      const dials = { ...scenario.dials };
      for (const i of cat.items) dials[i.id] = i.defaultDialPct;
      edit({ ...scenario, dials }, catKey);
    },

    resetItem(itemId) {
      const { scenario, allCats } = get();
      if (!scenario) return;
      const item = allCats.flatMap(c => c.items).find(i => i.id === itemId);
      edit({ ...scenario, dials: { ...scenario.dials, [itemId]: item?.defaultDialPct ?? 100 } });
    },

    resetAllDials() {
      const { scenario } = get();
      if (!scenario) return;
      edit({ ...scenario, dials: {} });
    },

    setEvents(events) {
      const { scenario } = get();
      if (!scenario) return;
      edit({ ...scenario, events });
    },

    addScenarioItem(item) {
      const { scenario } = get();
      if (!scenario) return;
      // `si-` prefixed so a what-if is distinguishable from a Notion expense id
      // anywhere downstream — `putDials` prunes against the expense table, and
      // an id that isn't there must be recognisable rather than merely absent.
      const id = `si-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      edit({
        ...scenario,
        items: [...(scenario.items ?? []), { ...item, id, monthly: Number(item.monthly) || 0 }],
      });
    },

    updateScenarioItem(id, patch) {
      const { scenario } = get();
      if (!scenario) return;
      edit({
        ...scenario,
        items: (scenario.items ?? []).map(i => (i.id === id ? { ...i, ...patch } : i)),
      });
    },

    removeScenarioItem(id) {
      const { scenario } = get();
      if (!scenario) return;
      // The dial goes with it. Leaving an orphan behind means re-adding a line
      // with the same id would silently inherit the old dial, and it would ride
      // along in every save forever.
      const dials = { ...scenario.dials };
      delete dials[id];
      edit({ ...scenario, dials, items: (scenario.items ?? []).filter(i => i.id !== id) });
    },

    setPriority(priority) {
      const { scenario } = get();
      if (!scenario) return;
      edit({ ...scenario, priority });
    },

    patchScenario(patch) {
      const { scenario } = get();
      if (!scenario) return;
      edit({ ...scenario, ...patch });
    },

    async saveScenario() {
      const { scenario } = get();
      if (!scenario) return;
      set({ saving: true });
      try {
        await pushScenario(scenario.id, scenario);
        clearDraft(scenario.id);
        set({ saved: scenario, dirty: false });
        await get().load();
      } finally {
        set({ saving: false });
      }
    },

    async saveScenarioAs(name) {
      const { scenario, saved } = get();
      if (!scenario) throw new Error('No scenario selected');
      const sourceId = scenario.id;
      set({ saving: true });
      try {
        // Clone server-side from the *saved* source, then overwrite the copy
        // with the working values. The source row is never touched, which is
        // the whole point — branching must not disturb what you branched from.
        const { id } = await api.createScenario(name, sourceId);
        await pushScenario(id, scenario);
        // The draft belonged to this branch; the source goes back to as-saved.
        clearDraft(sourceId);
        if (saved) set({ scenario: saved, dirty: false });
        await get().load();
        get().selectScenario(id);
        return id;
      } finally {
        set({ saving: false });
      }
    },

    discardDraft() {
      const { saved } = get();
      if (!saved) return;
      clearDraft(saved.id);
      set({ scenario: saved, dirty: false });
      remerge();
      recompute();
    },

    autoBalance(mode = 'proportional') {
      const { allCats, scenario, data } = get();
      if (!scenario || !data) return;
      const basis = getNetBasis();
      const dials = autoBalance(allCats, scenario.dials, {
        // Same take-home-after-deductions income the live cap uses, so baking
        // the trim in lands the plan where the meter says it should.
        income: takeHomeIncome(incomeOn(data, basis), allCats, scenario.dials, basis),
        priority: scenario.priority,
        mode,
        basis,
      });
      // keep the pre-balance state so the button is reversible
      set({ undoDials: { ...scenario.dials } });
      edit({ ...scenario, dials });
    },

    undoAutoBalance() {
      const { scenario, undoDials } = get();
      if (!scenario || !undoDials) return;
      set({ undoDials: null });
      edit({ ...scenario, dials: undoDials });
    },

    setSetting(key, value) {
      const { data } = get();
      if (data) set({ data: { ...data, settings: { ...data.settings, [key]: value } } });
      api.putSettings({ [key]: value }).catch(e => console.error('setting save failed', e));
    },

    /**
     * Re-run the cap because the axis changed, without touching intent.
     *
     * Deliberately not routed through `edit()`: a basis flip is a change of
     * units, not a change of plan. Sending it through `edit()` would mark the
     * scenario dirty, schedule a draft write, and make "I looked at the same
     * budget a different way" indistinguishable from "I edited my budget" —
     * with a Save button lit up over an edit nobody made.
     */
    onBasisChange() {
      recompute();
    },

    async sync() {
      set({ syncing: true, syncWarning: null });
      try {
        // Pulls Notion and re-reconciles YNAB against it in one call — see
        // worker/lib/refresh.ts for why those are no longer separable.
        const r = await api.refresh();
        await get().load();
        if (r.ynabError) set({ syncWarning: `YNAB refresh failed: ${r.ynabError}` });
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e) });
      } finally {
        set({ syncing: false });
      }
    },
  };
});

/*
 * The cap follows the axis.
 *
 * Subscribed once at module scope rather than from a component effect: the
 * effective dials are store state that several pages read, and tying their
 * correctness to whether some particular component happens to be mounted is how
 * you get a Dashboard showing figures trimmed against the ceiling the
 * Projections page was last looking at.
 */
subscribeNetBasis(() => useStore.getState().onBasisChange());
